/**
 * Step list factory for the Interactive Viewer tours.
 *
 * Both the In Progress and Asbuilds tours walk the user through the same
 * Interactive Viewer surface (layout dropdown → marker → photo carousel →
 * hotspots tab → viewer container), so they share this step list verbatim
 * and only differ in the welcome step's copy.
 *
 * Targeting hooks live as ``data-tour-id`` attributes / well-known
 * selectors on the in-app components:
 *
 *   - ``iv-layout-select``         → layout dropdown wrapper (TopControls)
 *   - ``.photo-marker``            → SVG photo markers (FloorPlanCanvas / PDFLayoutViewer)
 *   - ``.photo-selection-tooltip`` → multi-date selector (PhotoSelectionTooltip)
 *   - ``iv-photo-tooltip-entry``   → individual date row in the tooltip
 *   - ``iv-panorama-dialog``       → 360° panorama Dialog content
 *   - ``iv-panorama-viewer``       → Pannellum panorama container
 *   - ``iv-panorama-fab``          → "+" FAB inside the panorama
 *   - ``iv-panorama-fab-dialog``   → FAB action Dialog content
 *   - ``iv-panorama-photo-nav``    → bottom-right photo prev/next block
 *   - ``iv-panorama-date-nav``     → bottom-right date prev/next block
 *   - ``iv-tab-hotspots``          → "Hotspots" tab button (TabLayout auto-emits)
 *   - ``iv-tab-layouts``           → "Layouts" tab button (TabLayout auto-emits)
 *   - ``iv-hotspots-table``        → wrapper around the hotspots table (HotspotsView)
 *   - ``iv-viewer-container``      → main viewer flex container (LayoutsView)
 */
import {
  clickAndWaitFor,
  clickPreferReact,
  pressEscape,
  waitForCondition,
} from '../utils/dom';
import type { TourStep } from '../types';
import {
  hasPhotoMarker,
  hasUnitPolygon,
  isFabDialogOpen,
  isHeatMapAvailable,
  isHeatMapPanelOpen,
  isHeatMapToggleVisible,
  isListboxOpen,
  isPanoramaOpen,
  isPhotoTooltipOpen,
  isTabActive,
  waitForLayoutMarkers,
} from './selectors';

export interface InteractiveViewerStepsOptions {
  /** Display name of the feature, used in the welcome step's title. */
  featureName: string;
  /**
   * Body copy for the welcome step — typically the one-sentence elevator
   * pitch for the feature ("This is the X feature, where you can …").
   */
  welcomeBody: string;
}

export function buildInteractiveViewerSteps({
  featureName,
  welcomeBody,
}: InteractiveViewerStepsOptions): TourStep[] {
  // Closure state for the unit-polygon step's auto-advance detection.
  //
  // ``selectedUnit`` is React state — there is no DOM class or attribute
  // that flips when a unit is selected (the selected polygon is excluded
  // entirely from the render loop). The cleanest signal we have is the
  // *count* of rendered polygons: it drops by one when a unit becomes
  // selected. We capture the count on first read (which doubles as the
  // TourContext "baseline" check) and trip on any decrease.
  let unitPolygonBaseline: number | null = null;
  const countUnitPolygons = (): number =>
    typeof document === 'undefined'
      ? 0
      : document.querySelectorAll('[data-tour-id="iv-unit-polygon"]').length;

  return [
    {
      id: 'welcome',
      title: `Welcome to ${featureName}`,
      body: welcomeBody,
    },
    {
      id: 'layout-select',
      target: '[data-tour-id="iv-layout-select"]',
      // 'right' keeps the tooltip out of the way of the dropdown popover
      // that opens directly below the trigger.
      placement: 'right',
      title: 'Pick an area',
      body: 'Select an area to view by clicking here.',
      onBeforeNext: () =>
        clickAndWaitFor(
          '[data-tour-id="iv-layout-select"] [role="combobox"]',
          '[role="listbox"]',
        ),
      autoAdvanceWhen: () => isListboxOpen(),
    },
    {
      id: 'layout-items',
      target: '[role="listbox"]',
      placement: 'right',
      spotlightPadding: 6,
      title: 'Choose an area',
      body: 'Click any area in the list, or press Next to keep your current selection.',
      // Path A — user clicks "Next":
      // The auto-start gate already verified the currently-loaded layout
      // has markers + photos. Don't pick a different option (the first
      // alphabetical option can easily be an empty layout that breaks
      // the rest of the tour). Just dispatch Escape to close the
      // dropdown without changing selection.
      //
      // Path B — user picks an option themselves:
      // ``autoAdvanceWhen`` handles that case. If the user picks an
      // empty layout, ``marker-unavailable`` correctly takes over.
      onBeforeNext: async () => {
        pressEscape();
        await waitForLayoutMarkers();
      },
      autoAdvanceWhen: async () => {
        if (isListboxOpen()) return false;
        await waitForLayoutMarkers();
        return true;
      },
    },
    // ─── Unit / area polygons (only rendered when the layout has them) ─────
    // Area-tag layouts split the floor plan into named areas; each area
    // owns the photo markers captured inside it. Clicking a polygon
    // sets ``selectedUnit``, which flips marker visibility from "markers
    // outside any area" to "markers inside the selected area". We always
    // show this step on layouts with polygons — even when outside-area
    // markers happen to be visible at step entry — so the user learns
    // about the area-click interaction before we spotlight a marker.
    {
      id: 'unit-polygon',
      target: '[data-tour-id="iv-unit-polygon"]',
      placement: 'right',
      spotlightPadding: 8,
      title: 'Click to enter an area',
      body:
        'This layout is divided into areas. Click any area on the floor ' +
        'plan to reveal the photo markers captured inside it.',
      skipIf: () => {
        if (!hasUnitPolygon()) return true;
        // (Re-)capture the baseline polygon count for auto-advance.
        // ``skipIf`` runs once per step entry, so this also handles
        // re-runs of the tour after a dismissal.
        unitPolygonBaseline = countUnitPolygons();
        return false;
      },
      onBeforeNext: async () => {
        clickPreferReact('[data-tour-id="iv-unit-polygon"]');
        // Wait for the marker visibility transition: clicking the polygon
        // hides outside-area markers and shows in-area ones, so we can't
        // just poll for ``hasPhotoMarker()`` (it might already be true
        // from outside markers). Wait for the polygon count to drop —
        // selecting a unit removes its polygon from the render loop —
        // and give the DOM a moment to commit the marker reflow.
        await waitForCondition(
          () =>
            unitPolygonBaseline !== null &&
            countUnitPolygons() < unitPolygonBaseline,
          4000,
          'unit polygon click → selected unit',
        );
        await waitForCondition(
          hasPhotoMarker,
          2000,
          'unit polygon click → in-area markers',
        );
      },
      // Auto-advance when the user clicks a polygon themselves.
      autoAdvanceWhen: () =>
        unitPolygonBaseline !== null &&
        countUnitPolygons() < unitPolygonBaseline,
    },
    // The marker step splits into two siblings; only one renders depending
    // on whether a photo marker is currently visible.
    {
      id: 'marker-available',
      target: '.photo-marker',
      placement: 'right',
      spotlightPadding: 12,
      title: 'Open a photo at a specific location',
      body:
        'Click on the numbered markers to view a photo in a given location ' +
        'on the available dates.',
      skipIf: () => !hasPhotoMarker(),
      // "Next" → activate the first marker on the user's behalf. The
      // marker click either opens the panorama directly (1 photo) or
      // shows the photo-selection tooltip (multiple photos). We wait
      // for *either* to mount so the next step's ``skipIf`` doesn't fire
      // too early and walk past the panorama branch.
      onBeforeNext: async () => {
        clickPreferReact('.photo-marker');
        await waitForCondition(
          () => isPhotoTooltipOpen() || isPanoramaOpen(),
          4000,
          'marker click → tooltip or panorama',
        );
      },
      autoAdvanceWhen: () => isPhotoTooltipOpen() || isPanoramaOpen(),
    },
    {
      id: 'marker-unavailable',
      title: 'Photo locations',
      body:
        'Once layouts are available, click on the numbered markers to view ' +
        'a photo in a given location on the available dates.',
      skipIf: () => hasPhotoMarker(),
    },
    // ─── Multi-date selection (only when the marker has more than one photo) ─
    {
      id: 'photo-selection',
      target: '.photo-selection-tooltip',
      placement: 'right',
      spotlightPadding: 6,
      title: 'Pick a date',
      body: 'Select a date to see the state of this area at that time.',
      // Skip when the location has only one photo — the panorama opens
      // directly without a date-picker tooltip.
      skipIf: () => !isPhotoTooltipOpen(),
      onBeforeNext: async () => {
        clickPreferReact('[data-tour-id="iv-photo-tooltip-entry"]');
        await waitForCondition(() => isPanoramaOpen(), 4000, 'photo entry → panorama');
      },
      autoAdvanceWhen: () => isPanoramaOpen(),
    },
    // ─── 360° panorama tour ────────────────────────────────────────────────
    {
      id: 'panorama-viewer',
      target: '[data-tour-id="iv-panorama-viewer"]',
      placement: 'top',
      spotlightPadding: 4,
      title: 'Look around',
      body:
        'Click and drag to look around. Scroll up and down on your mouse / ' +
        'trackpad to zoom in and out.',
      skipIf: () => !isPanoramaOpen(),
    },
    {
      id: 'panorama-fab',
      target: '[data-tour-id="iv-panorama-fab"]',
      placement: 'right',
      spotlightPadding: 8,
      title: 'Add a hotspot',
      body:
        'Use this button to pin a punch item, observation, or RFI to the ' +
        'exact spot you\u2019re looking at.',
      skipIf: () => !isPanoramaOpen(),
      onBeforeNext: async () => {
        clickPreferReact('[data-tour-id="iv-panorama-fab"]');
        await waitForCondition(() => isFabDialogOpen(), 4000, 'FAB click → action dialog');
      },
      autoAdvanceWhen: () => isFabDialogOpen(),
    },
    {
      id: 'panorama-fab-dialog',
      target: '[data-tour-id="iv-panorama-fab-dialog"]',
      placement: 'left',
      spotlightPadding: 4,
      title: 'Pick an item type',
      body:
        'Choose what kind of item to pin to this location. Each kind has ' +
        'its own short form to fill in.',
      skipIf: () => !isFabDialogOpen(),
      // Close the FAB dialog before moving on to the navigation controls.
      onBeforeNext: () => pressEscape(),
    },
    {
      id: 'panorama-photo-nav',
      target: '[data-tour-id="iv-panorama-photo-nav"]',
      placement: 'left',
      spotlightPadding: 6,
      title: 'Step through photo locations',
      body:
        'Use these arrows to walk forward and backward through other ' +
        'photo locations captured on this same date.',
      skipIf: () => !isPanoramaOpen(),
    },
    {
      id: 'panorama-date-nav',
      target: '[data-tour-id="iv-panorama-date-nav"]',
      placement: 'left',
      spotlightPadding: 6,
      title: 'Compare across dates',
      body:
        'Use these arrows to see this same location at different points in ' +
        'time as construction progresses.',
      skipIf: () => !isPanoramaOpen(),
      // Close the panorama so the next step lands back on the layouts page.
      onBeforeNext: () => pressEscape(),
    },
    // ─── Heat map ──────────────────────────────────────────────────────────
    // The heat map is opt-in and dormant by default: the bottom-right
    // button is its entry point, and clicking it activates the overlay
    // (area/marker coloring + legend). We only spotlight it once the
    // panorama is closed (the previous step's onBeforeNext takes care of
    // that).
    {
      id: 'heatmap-button',
      target: '[data-tour-id="iv-heatmap-toggle"]',
      placement: 'left',
      spotlightPadding: 8,
      title: 'Turn on the heat map',
      body:
        'The heat map colors each area by how many open issues — ' +
        'observations, punch items, RFIs, submittals — are tied to it. ' +
        'Click the button to turn it on.',
      // Skip when the layout doesn't have heat-map support, or when the
      // panel is already open from a previous interaction.
      skipIf: () => !isHeatMapAvailable() || isHeatMapPanelOpen(),
      onBeforeNext: async () => {
        if (isHeatMapToggleVisible()) {
          clickPreferReact('[data-tour-id="iv-heatmap-toggle"]');
          await waitForCondition(
            isHeatMapPanelOpen,
            2000,
            'heatmap toggle → panel',
          );
        }
      },
      autoAdvanceWhen: () => isHeatMapPanelOpen(),
    },
    {
      id: 'heatmap-panel',
      target: '[data-tour-id="iv-heatmap-panel"]',
      placement: 'left',
      spotlightPadding: 8,
      title: 'Filter by issue density',
      body:
        'Click any color band to show only areas in that density range — ' +
        'great for focusing on the worst spots. Click the same band again, ' +
        'or hit "Clear filter", to reset.',
      skipIf: () => !isHeatMapPanelOpen(),
    },
    // ─── Hotspots tab + viewer ─────────────────────────────────────────────
    {
      id: 'hotspots-tab',
      target: '[data-tour-id="iv-tab-hotspots"]',
      placement: 'bottom',
      title: 'Hotspots',
      body:
        'Click here to view hotspots for the selected area. Hotspots can ' +
        'show items such as punch items, observations, or RFIs within 3D space.',
      onBeforeNext: () => clickPreferReact('[data-tour-id="iv-tab-hotspots"]'),
      autoAdvanceWhen: () => isTabActive('hotspots'),
    },
    {
      id: 'hotspots-table',
      target: '[data-tour-id="iv-hotspots-table"]',
      placement: 'top',
      spotlightPadding: 4,
      title: 'Browse all hotspots',
      body:
        'Every hotspot for this project lives here. Search, filter, and ' +
        'click any row to jump to it in the viewer.',
      onBeforeNext: () => clickPreferReact('[data-tour-id="iv-tab-layouts"]'),
    },
    {
      id: 'viewer-zoom',
      target: '[data-tour-id="iv-viewer-container"]',
      placement: 'top',
      spotlightPadding: 4,
      title: 'Zoom in and out',
      body: 'Scroll up or down in the viewer to zoom in and out where your cursor is.',
    },
  ];
}
